import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import {
  Button,
  DialogActions,
  DialogContent,
  Divider,
  FormHelperText,
  Grid,
  Typography,
  Box
} from '@mui/material';
import FMSModal from 'src/common/FMSModal';
import Loader from 'src/common/Loader';
import { Form, Formik } from 'formik';
import { PASSWORD_REGEX } from 'src/utils/constants';
import { APIService } from 'src/api/api';
import { setSuccessInfo } from 'src/redux/modalSlice';
import { useDispatch } from 'react-redux';
import InputField from './formFields/InputField';

const resetPasswordFormModel = {
  email: {
    name: 'email',
    label: 'Email',
    type: 'text'
  },
  password: {
    name: 'new_password',
    label: 'Password',
    requiredErrorMsg: 'Password is required',
    invalidErrorMsg:
      'Must contain 8 characters, one uppercase, one lowercase, one number and one special character',
    type: 'password'
  },
  confirmPassword: {
    name: 'confirm_password',
    label: 'Confirm Password',
    requiredErrorMsg: 'Confirm Password is required',
    invalidErrorMsg: 'Passwords must match',
    type: 'password'
  }
};

const { email, password, confirmPassword } = resetPasswordFormModel;

export const resetPasswordValidationSchema = Yup.object().shape({
  [password.name]: Yup.string()
    .matches(PASSWORD_REGEX, password.invalidErrorMsg)
    .required(password.requiredErrorMsg),
  [confirmPassword.name]: Yup.string()
    .required(confirmPassword.requiredErrorMsg)
    .oneOf([Yup.ref(password.name), null], confirmPassword.invalidErrorMsg)
});

function PasswordResetModal({ open, onClose, userEmail }) {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const requiredHintText = '';

  const initialValues = {
    [email.name]: userEmail,
    [password.name]: '',
    [confirmPassword.name]: ''
  };

  useEffect(() => {
    if (open) {
      setErrorMsg(null);
    }
  }, [open]);

  const onSubmit = async (values, actions) => {
    setLoading(true);
    try {
      const res = await APIService.resetPassword(values);
      if (!res.success) throw res;

      onClose();

      dispatch(
        setSuccessInfo({
          successMsg: 'Password reset successful!'
        })
      );
    } catch (error) {
      actions.setSubmitting(false);
      setErrorMsg('Unable to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <FMSModal open={open} maxWidth="sm" fullWidth onClose={onClose}>

      {/* Modern Header */}
      <Box
        sx={{
          px: 3,
          py: 2.5,
          borderBottom: '1px solid #E5E7EB'
        }}
      >
        <Typography fontWeight={700} fontSize={18}>
          Reset Password
        </Typography>
      </Box>

      <Formik
        initialValues={initialValues}
        validationSchema={resetPasswordValidationSchema}
        onSubmit={onSubmit}
      >
        {({ isSubmitting }) => (loading ? (
          <Loader />
        ) : (
          <Form id="reset-password-form">

            <DialogContent sx={{ px: 3, py: 3 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                * Required fields
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <InputField
                    name={email.name}
                    label={email.label}
                    disabled
                    fullWidth
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <InputField
                    name={password.name}
                    label={`${password.label} *`}
                    type={password.type}
                    required
                    requiredHintText={requiredHintText}
                    fullWidth
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <InputField
                    name={confirmPassword.name}
                    label={`${confirmPassword.label} *`}
                    type={confirmPassword.type}
                    required
                    requiredHintText={requiredHintText}
                    fullWidth
                  />
                </Grid>
              </Grid>

              {errorMsg && (
              <FormHelperText error sx={{ mt: 2 }}>
                {errorMsg}
              </FormHelperText>
              )}
            </DialogContent>

            <Divider />

            <DialogActions
              sx={{
                p: 2.5,
                justifyContent: 'flex-end',
                gap: 2
              }}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={onClose}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                Cancel
              </Button>

              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isSubmitting}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3
                }}
              >
                Reset Password
              </Button>
            </DialogActions>
          </Form>
        ))}
      </Formik>
    </FMSModal>
  );
}

PasswordResetModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  userEmail: PropTypes.string
};

export default PasswordResetModal;
